<template>
  <div class="edit-media-page">
    <div class="edit-media-page-header mt-3 px-3">
      <b-button variant="outline-light" size="sm" @click="handleBack">
        <font-awesome-icon icon="arrow-left" class="mr-2"/>
        Back
      </b-button>
    </div>
    <EditMedia v-if="editorReady" :media-id="mediaId" @close="handleClose" @save="saveMedia($event)"/>
    <PublishCheckResults ref="PublishModal" @save="saveMedia($event)"/>
    <SimpleModal :value="confirmCloseVisible" @ok="onConfirmDiscard" @cancel="onCancelDiscard" msg="There are unsaved edits. Are you sure you want to leave and discard all changes?"/>
  </div>
</template>

<script>
import EditMedia from "../components/EditMedia.vue"
import PublishCheckResults from "../components/PublishCheckResults.vue"
import SimpleModal from "../components/SimpleModal.vue"

export default {
  name: "EditMediaPage",

  components: {
    EditMedia,
    PublishCheckResults,
    SimpleModal
  },

  data() {
    return {
      editorReady: false,
      confirmCloseVisible: false,
      pendingNext: null,
      returnRoute: null
    }
  },

  computed: {
    mediaId() {
      return this.$route.params.id
    },

    hasUnsavedEdits() {
      return !!this.$store.getters['media/editDiff'](this.mediaId)
    }
  },

  watch: {
    '$route.params.id'(newId, oldId) {
      if (newId && newId !== oldId) {
        if (oldId) this.$store.commit('media/clearEditItem', oldId)
        this.loadEditor(newId)
      }
    }
  },

  methods: {
    async loadEditor(id) {
      this.editorReady = false
      try {
        await this.$store.dispatch('media/showEditor', id)
        this.editorReady = true
      }
      catch (error) {
        this.$root.app.showAlert(`Failed to load item: ${error.text || error}`, "danger")
        this.$router.push({ name: 'categories' })
      }
    },

    handleClose() {
      this.goBack()
    },

    handleBack() {
      this.goBack()
    },

    // Return to the list the user came from with an explicit push rather than
    // $router.back(). $router.back() is a popstate navigation: the browser URL
    // has already changed by the time beforeRouteLeave runs, so cancelling the
    // discard-confirm modal (next(false)) leaves the URL and the view out of
    // sync (a vue-router v3 bug), which then breaks every subsequent Back.
    // An explicit push is a normal navigation the guard can cancel cleanly.
    // For deep links / fresh tabs there is no captured origin, so fall back to
    // the categories root.
    goBack() {
      this.$router.push(this.returnRoute || { name: 'categories' })
    },

    onConfirmDiscard() {
      const next = this.pendingNext
      this.pendingNext = null
      this.confirmCloseVisible = false
      this.$store.commit('media/clearEditItem', this.mediaId)
      if (next) next()
    },

    onCancelDiscard() {
      const next = this.pendingNext
      this.pendingNext = null
      this.confirmCloseVisible = false
      if (next) next(false)
    },

    saveMedia({ id, skipPublishCheck }) {
      this.$store.dispatch('media/saveEdit', { id, skipPublishCheck })
        .then(result => {
          if (result == "no-change") {
            this.$root.app.showAlert("Nothing to save", "warning")
          }
          else {
            this.$root.app.showAlert("Save successful", "success")
          }
        })
        .catch(({ status, json }) => {
          if (status == 400) {
            if (json.type == 'published_but_not_ready') {
              this.$store.dispatch('media/publishCheckResults', {
                status: "error",
                results: json.data,
                payload: id,
                isSaveResult: true
              })
            }
            else {
              this.$root.app.showAlert(`An error occured while saving: ${json.data}`, "danger")
            }
          }
          else {
            this.$root.app.showErrorDialog(json, status)
          }
        })
    },

    beforeUnloadHandler(e) {
      if (this.hasUnsavedEdits) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle("Edit", false)
    this.$root.$emit('set-toolbar', 'empty-toolbar')
    this.loadEditor(this.mediaId)
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
  },

  beforeRouteEnter(to, from, next) {
    // from.name is null on a fresh page load (typed URL, external link, new
    // tab). A real route name means the user navigated here from within the
    // app; capture it so Back returns to the exact list (media vs categories)
    // with its filters/query preserved, instead of guessing.
    const origin = from.name
      ? { name: from.name, params: from.params, query: from.query }
      : null
    next(vm => { vm.returnRoute = origin })
  },

  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedEdits) {
      this.pendingNext = next
      this.confirmCloseVisible = true
    }
    else {
      this.$store.commit('media/clearEditItem', this.mediaId)
      next()
    }
  }
}
</script>
